import React, { useState, useRef } from 'react'

const FRONTS = 5
const TROOPS = 100
const EXPLORE = 0.2 // how often the AI plays off-model so it stays unreadable

// Uniform random composition of TROOPS into FRONTS parts.
const randomAllocation = () => {
  const cuts = Array.from({ length: FRONTS - 1 }, () => Math.floor(Math.random() * (TROOPS + 1)))
  cuts.sort((a, b) => a - b)
  const out = []
  let prev = 0
  for (const c of cuts) {
    out.push(c - prev)
    prev = c
  }
  out.push(TROOPS - prev)
  return out
}

/*
 * Fictitious play. The AI treats your past deployments as your mixed strategy
 * and computes an exact best response to that empirical distribution.
 *
 * value[i][t] = fraction of your history where front i was held with fewer
 * than t troops, i.e. the probability that committing t troops there wins it.
 * A backward DP over fronts then finds the allocation maximising expected
 * fronts won -- the true best response, not a heuristic.
 */
const bestResponse = (history) => {
  if (!history.length || Math.random() < EXPLORE) return randomAllocation()

  const value = Array.from({ length: FRONTS }, (_, i) =>
    Array.from({ length: TROOPS + 1 }, (_, t) =>
      history.reduce((n, past) => n + (past[i] < t ? 1 : 0), 0) / history.length
    )
  )

  // dp[i][r] = best expected wins from fronts i..FRONTS-1 given r troops left
  const dp = Array.from({ length: FRONTS + 1 }, () => new Float64Array(TROOPS + 1))
  const pick = Array.from({ length: FRONTS }, () => new Int16Array(TROOPS + 1))

  for (let i = FRONTS - 1; i >= 0; i--) {
    for (let r = 0; r <= TROOPS; r++) {
      let best = -1
      let bestT = 0
      for (let t = 0; t <= r; t++) {
        const score = value[i][t] + dp[i + 1][r - t]
        if (score > best) {
          best = score
          bestT = t
        }
      }
      dp[i][r] = best
      pick[i][r] = bestT
    }
  }

  const alloc = []
  let remaining = TROOPS
  for (let i = 0; i < FRONTS; i++) {
    const t = pick[i][remaining]
    alloc.push(t)
    remaining -= t
  }

  // Once a front is out of reach of everything in your history, extra troops
  // there buy nothing, so the DP leaves them unspent. Against a player who
  // deviates they are still worth something -- push the surplus onto the fronts
  // the DP conceded, cheapest first, so the AI always fields all 100.
  const order = alloc.map((t, i) => i).sort((a, b) => alloc[a] - alloc[b])
  for (let k = 0; remaining > 0; k = (k + 1) % FRONTS) {
    alloc[order[k]] += 1
    remaining -= 1
  }
  return alloc
}

const ColonelBlotto = ({ onFinish }) => {
  const history = useRef([]) // your past allocations, the AI's model of you
  const [alloc, setAlloc] = useState([20, 20, 20, 20, 20])
  const [outcome, setOutcome] = useState(null)
  const [record, setRecord] = useState({ won: 0, lost: 0, drew: 0 })

  const used = alloc.reduce((a, b) => a + b, 0)
  const remaining = TROOPS - used

  const setFront = (i, raw) => {
    const capped = Math.max(0, Math.min(raw, alloc[i] + remaining))
    setAlloc(alloc.map((v, j) => (j === i ? capped : v)))
  }

  const deploy = () => {
    if (remaining !== 0) return

    const ai = bestResponse(history.current)
    history.current = [...history.current, alloc]

    let youWon = 0
    let aiWon = 0
    const fronts = alloc.map((mine, i) => {
      const theirs = ai[i]
      const winner = mine > theirs ? 'you' : theirs > mine ? 'ai' : 'tie'
      if (winner === 'you') youWon++
      if (winner === 'ai') aiWon++
      return { mine, theirs, winner }
    })

    const result = youWon > aiWon ? 'win' : aiWon > youWon ? 'loss' : 'draw'
    setOutcome({ fronts, youWon, aiWon, result })
    setRecord((r) => ({
      won: r.won + (result === 'win' ? 1 : 0),
      lost: r.lost + (result === 'loss' ? 1 : 0),
      drew: r.drew + (result === 'draw' ? 1 : 0),
    }))
    onFinish({ game: 'Colonel Blotto', result, detail: `${youWon}–${aiWon} fronts` })
  }

  const battles = record.won + record.lost + record.drew

  return (
    <div className="game">
      <div className="game__board">
        <div className="blotto">
          {alloc.map((v, i) => {
            const f = outcome ? outcome.fronts[i] : null
            return (
              <div key={i} className={`front ${f ? `front--${f.winner}` : ''}`}>
                <div className="front__head">
                  <span>Front {i + 1}</span>
                  <strong>{v}</strong>
                </div>
                <input
                  type="range"
                  min="0"
                  max={TROOPS}
                  value={v}
                  onChange={(e) => setFront(i, parseInt(e.target.value, 10))}
                />
                {f && (
                  <div className="front__result">
                    <span className="front__you" style={{ width: `${f.mine}%` }}>{f.mine}</span>
                    <span className="front__ai" style={{ width: `${f.theirs}%` }}>{f.theirs}</span>
                  </div>
                )}
              </div>
            )
          })}
        </div>

        <div className="game__actions">
          <span className={`remaining ${remaining === 0 ? 'remaining--ok' : ''}`}>
            {remaining === 0 ? 'All 100 deployed' : `${remaining} troops unassigned`}
          </span>
          <button className="btn btn-primary" onClick={deploy} disabled={remaining !== 0}>
            Deploy
          </button>
          <button className="btn btn-ghost" onClick={() => setAlloc([20, 20, 20, 20, 20])}>
            Even split
          </button>
        </div>
      </div>

      <aside className="game__panel">
        <h4>Battle {battles + 1}</h4>
        <div className="scoreline">
          <div><span>Won</span><strong className="positive">{record.won}</strong></div>
          <div><span>Drawn</span><strong className="muted">{record.drew}</strong></div>
          <div><span>Lost</span><strong className="negative">{record.lost}</strong></div>
        </div>

        {outcome && (
          <p className="verdict">
            {outcome.result === 'win'
              ? `You took ${outcome.youWon} of 5 fronts.`
              : outcome.result === 'loss'
              ? `The AI took ${outcome.aiWon} of 5 fronts.`
              : `Split ${outcome.youWon}–${outcome.aiWon}.`}
          </p>
        )}

        <h5>How the AI plays</h5>
        <p className="note">
          It treats your {history.current.length} past {history.current.length === 1 ? 'deployment' : 'deployments'} as
          your mixed strategy and solves a dynamic program for the exact best
          response &mdash; the split that maximises expected fronts won against
          your own tendencies.
        </p>
        <p className="note note--rule">
          Blotto has no pure Nash equilibrium: every fixed allocation is beaten by
          some other. Repeat a favourite shape and the DP will find it. One battle
          in five the AI deploys at random, so you cannot read it either.
        </p>
      </aside>
    </div>
  )
}

export default ColonelBlotto
